// Event and attribute names the console reads, in one place (CLAUDE.md: mobile semantic
// conventions are not stable, so a rename is a one-line change). Source: apsio-spec v0.1
// (sections 6 and 8, the registry) and the OpenTelemetry HTTP conventions for client spans.
export const EVENT = {
  crash: 'app.crash',
  hang: 'apsio.hang',
  abnormalExit: 'app.abnormal_exit',
  breadcrumb: 'apsio.breadcrumb',
  sessionStart: 'session.start',
  sessionSummary: 'apsio.session.summary',
} as const

export const SPAN = {
  start: 'app.start',
  screenLoad: 'app.screen.load',
} as const

export const ATTR = {
  /** The app user's hash (spec 13: setUser), on the records written while it is set. */
  userHash: 'user.hash',
  startType: 'app.start.type',
  screenName: 'app.screen.name',
  hangDuration: 'apsio.hang.duration',
  /** `launch` on a hang that began before the app first became active (spec 6.3, APS-531). */
  hangPhase: 'apsio.hang.phase',
  exceptionType: 'exception.type',
  exceptionMessage: 'exception.message',
  breadcrumbCategory: 'apsio.breadcrumb.category',
  httpMethod: 'http.request.method',
  httpRoute: 'http.route',
  httpStatus: 'http.response.status_code',
  urlTemplate: 'url.template',
  urlPath: 'url.path',
  urlFull: 'url.full',
  serverAddress: 'server.address',
} as const

/** Resource attributes the session summary carries (spec section 2). */
export const RESOURCE = {
  sessionId: 'session.id',
  serviceVersion: 'service.version',
  buildId: 'app.build_id',
  deviceModel: 'device.model.identifier',
  osName: 'os.name',
  osVersion: 'os.version',
  installationId: 'app.installation.id',
} as const

/** Explore's own field names for a record's columns (apps/api read/explore.ts LOG_FIELDS). */
export const LOG_FIELD = {
  eventName: 'event.name',
  /** The record's logger: its InstrumentationScope name (spec 13.6). */
  scopeName: 'scope.name',
} as const

/** OTLP severity number of ERROR (spec 6.2). */
export const SEVERITY_ERROR = 17

/** Session outcomes (spec 3.4). */
export const OUTCOME = {
  active: 'active',
  exited: 'exited',
  crashed: 'crashed',
  abnormalExit: 'abnormal_exit',
} as const

/**
 * The ingest host of an app key (D5). A staging console (VITE_INGEST_HOST, set by
 * scripts/deploy-web.sh) shows its environment's generic host instead, which takes the key in the
 * Apsio-App-Key header (D37, APS-478): staging has no per-app hosts.
 */
export const ingestHost = (key: string): string =>
  import.meta.env.VITE_INGEST_HOST || `${key}.ingest.apsio.cloud`

/** Images that are the system's, not the app's: their frames are folded away by default. */
const SYSTEM_IMAGE =
  /^(libobjc|libsystem_|libdispatch|libswift|libc\+\+|libdyld|dyld|UIKit|UIKitCore|SwiftUI|Foundation|CoreFoundation|GraphicsServices|QuartzCore|CFNetwork|AttributeGraph|libart|libc\.so|framework\.jar|boot\.oat|libandroid_runtime|\[unknown\])/
export const isSystemImage = (image: string | null | undefined): boolean =>
  !!image && SYSTEM_IMAGE.test(image)

/** A feature flag as a session attribute: feature_flag.<name> = <variant> (spec 13). */
export const FLAG_ATTR_PREFIX = 'feature_flag.'
